import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Breadcrumbs,
  Link,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  CloudUpload as UploadIcon,
  MoreVert as MoreVertIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  DriveFileRenameOutline as RenameIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import documentsService, { Document } from '../services/documentsService';
import { setDocuments } from '../store/slices/documentsSlice';
import { useConfirm } from '../contexts/ConfirmDialogContext';

const Documents: React.FC = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const documents = useSelector((state: RootState) => state.documents.documents);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolder, setCurrentFolder] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: number; name: string }>>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  // Rename dialog
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Move dialog — lets the user browse folders (starting at Root) and drop
  // the selected document into whichever one they land on.
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetFolder, setMoveTargetFolder] = useState<number | null>(null);
  const [moveBreadcrumbs, setMoveBreadcrumbs] = useState<Array<{ id: number; name: string }>>([]);
  const [moveFolders, setMoveFolders] = useState<Document[]>([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');

  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const userId = user?.user_id || '';

  useEffect(() => {
    fetchDocuments();
  }, [currentFolder]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentsService.getDocuments(userId || '', currentFolder || undefined); // Replace 1 with actual user ID
      dispatch(setDocuments(data));
      if (currentFolder) {
        const path = await documentsService.getBreadcrumbPath(currentFolder);
        setBreadcrumbs(Array.isArray(path) ? path : []);
      } else {
        setBreadcrumbs([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));

    try {
      await documentsService.uploadFiles(formData, currentFolder || undefined);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const handleCreateFolder = async () => {
    try {
      await documentsService.createFolder({
        name: 'New Folder',
        type: 'folder',
        parent_id: currentFolder || undefined,
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  // Opens the "..." menu for a document without also triggering whatever
  // sits underneath it (folder-open on the row/card click) — every entry
  // point into this menu must stop propagation, or clicking the button on
  // a folder both opens the menu AND navigates into that folder.
  const openDocumentMenu = (event: React.MouseEvent<HTMLElement>, doc: Document) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedDocument(doc);
  };

  const handleDocumentAction = (document: Document, action: string) => {
    setSelectedDocument(document);
    switch (action) {
      case 'rename':
        setRenameValue(document.name);
        setRenameDialogOpen(true);
        break;
      case 'move':
        openMoveDialog();
        break;
      case 'share':
        handleShareDocument(document);
        break;
      case 'delete':
        handleDeleteDocument(document);
        break;
    }
    setAnchorEl(null);
  };

  const handleShareDocument = async (document: Document) => {
    try {
      await documentsService.shareDocument(document.id, !document.is_shared);
      fetchDocuments();
    } catch (error) {
      console.error('Error sharing document:', error);
    }
  };

  const handleDeleteDocument = async (document: Document) => {
    const isFolder = document.type === 'folder';
    const ok = await confirm(
      isFolder
        ? `Delete the folder "${document.name}" and everything inside it? This can't be undone.`
        : `Delete "${document.name}"? This can't be undone.`
    );
    if (!ok) return;

    try {
      await documentsService.deleteDocument(document.id, userId); // Replace 1 with actual user ID
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleRenameSubmit = async () => {
    if (!selectedDocument || !renameValue.trim()) return;
    try {
      setRenaming(true);
      await documentsService.updateDocument({ id: selectedDocument.id, name: renameValue.trim() });
      setRenameDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error renaming document:', error);
    } finally {
      setRenaming(false);
    }
  };

  const loadMoveFolder = async (folderId: number | null, crumbs: Array<{ id: number; name: string }>) => {
    try {
      setMoveLoading(true);
      const data = await documentsService.getDocuments(userId || '', folderId || undefined);
      setMoveFolders((Array.isArray(data) ? data : []).filter((d: Document) => d.type === 'folder'));
      setMoveTargetFolder(folderId);
      setMoveBreadcrumbs(crumbs);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setMoveLoading(false);
    }
  };

  const openMoveDialog = () => {
    setMoveError('');
    setMoveDialogOpen(true);
    loadMoveFolder(null, []);
  };

  const handleMoveHere = async () => {
    if (!selectedDocument) return;
    try {
      setMoving(true);
      setMoveError('');
      await documentsService.moveDocument(selectedDocument.id, moveTargetFolder);
      setMoveDialogOpen(false);
      fetchDocuments();
    } catch (error: any) {
      setMoveError(error?.response?.data?.message || 'Error moving document');
    } finally {
      setMoving(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query) {
      try {
        const results = await documentsService.searchDocuments(userId, query); // Replace 1 with actual user ID
        dispatch(setDocuments(results));
      } catch (error) {
        console.error('Error searching documents:', error);
      }
    } else {
      fetchDocuments();
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Documents</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'primary' : 'default'}>
            <GridViewIcon />
          </IconButton>
          <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'primary' : 'default'}>
            <ListViewIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            component="label"
          >
            Upload
            <input
              type="file"
              hidden
              multiple
              onChange={handleUpload}
            />
          </Button>
          <Button
            variant="outlined"
            startIcon={<FolderIcon />}
            onClick={handleCreateFolder}
          >
            New Folder
          </Button>
        </Box>
      </Box>

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => setCurrentFolder(null)}
          sx={{ cursor: 'pointer' }}
        >
          Root
        </Link>
        {breadcrumbs.map((crumb) => (
          <Link
            key={crumb.id}
            component="button"
            variant="body1"
            onClick={() => setCurrentFolder(crumb.id)}
            sx={{ cursor: 'pointer' }}
          >
            {crumb.name}
          </Link>
        ))}
      </Breadcrumbs>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'grid' ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
          {documents?.map((doc: Document) => (
            <Paper
              key={doc.id}
              sx={{
                position: 'relative',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => doc.type === 'folder' && setCurrentFolder(doc.id)}
            >
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: 4, right: 4 }}
                onClick={(e) => openDocumentMenu(e, doc)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              {doc.type === 'folder' ? (
                <FolderIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              ) : (
                <FileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              )}
              <Typography variant="subtitle1" noWrap sx={{ mt: 1, width: '100%', textAlign: 'center' }}>
                {doc.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.type === 'file' ? `${(doc.size || 0) / 1024} KB` : ''}
              </Typography>
            </Paper>
          ))}
        </Box>
      ) : (
        <List>
          {documents?.map((doc: Document) => (
            <ListItem
              key={doc.id}
              component="div"
              sx={{ cursor: 'pointer' }}
              onClick={() => doc.type === 'folder' && setCurrentFolder(doc.id)}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={(e) => openDocumentMenu(e, doc)}
                >
                  <MoreVertIcon />
                </IconButton>
              }
            >
              <ListItemIcon>
                {doc.type === 'folder' ? (
                  <FolderIcon color="primary" />
                ) : (
                  <FileIcon color="action" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={doc.name}
                secondary={`${doc.type === 'file' ? `${(doc.size || 0) / 1024} KB` : ''} • ${new Date(doc.created_at).toLocaleDateString()}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => selectedDocument && handleDocumentAction(selectedDocument, 'rename')}>
          <ListItemIcon>
            <RenameIcon fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDocumentAction(selectedDocument, 'move')}>
          <ListItemIcon>
            <MoveIcon fontSize="small" />
          </ListItemIcon>
          Move
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDocumentAction(selectedDocument, 'share')}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          {selectedDocument?.is_shared ? 'Unshare' : 'Share'}
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDocumentAction(selectedDocument, 'delete')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Rename {selectedDocument?.type === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRenameSubmit}
            disabled={renaming || !renameValue.trim()}
          >
            {renaming ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move "{selectedDocument?.name}"</DialogTitle>
        <DialogContent>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => loadMoveFolder(null, [])}
              sx={{ cursor: 'pointer' }}
            >
              Root
            </Link>
            {moveBreadcrumbs.map((crumb, idx) => (
              <Link
                key={crumb.id}
                component="button"
                variant="body2"
                onClick={() => loadMoveFolder(crumb.id, moveBreadcrumbs.slice(0, idx + 1))}
                sx={{ cursor: 'pointer' }}
              >
                {crumb.name}
              </Link>
            ))}
          </Breadcrumbs>

          {moveLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {moveFolders
                .filter((f) => f.id !== selectedDocument?.id)
                .map((folder) => (
                  <ListItemButton
                    key={folder.id}
                    onClick={() => loadMoveFolder(folder.id, [...moveBreadcrumbs, { id: folder.id, name: folder.name }])}
                  >
                    <ListItemIcon>
                      <FolderIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={folder.name} />
                  </ListItemButton>
                ))}
              {moveFolders.filter((f) => f.id !== selectedDocument?.id).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  No subfolders here.
                </Typography>
              )}
            </List>
          )}

          {moveError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {moveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMoveHere} disabled={moving}>
            {moving ? 'Moving…' : 'Move Here'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;
